import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { useForm, FormProvider } from 'react-hook-form'
import styled from 'styled-components'
import produce from 'immer'
import PropTypes from 'prop-types'
import _ from 'lodash'
import * as uuid from 'uuid'

import { colours } from '@qflow/theme'

import { useRecordDetailData } from 'portal/components/RecordDetails'
import {
  ApplyOrCancelControls,
  useLineItemEditMixpanelEvents
} from 'portal/components/RecordEditing'
import { useProductGroupsLoader } from 'portal/components/RecordsModules/common'
import {
  LimitWidthContainer,
  MainSection,
  SubSection
} from 'portal/components/RecordsModules/common/StyledComponents'

import { cleanWasteRecordForUpdate } from 'portal/components/RecordsModules/Edit/Waste'

import {
  useModuleIds,
  useMountEffect,
  useUserProjectRole
} from 'portal/lib/hooks'
import { imperial } from 'portal/lib/functions'
import { Flex, Spinner, SubHeading, Text } from 'portal/lib/primitives'

import { projectGetDetailsSaga } from 'portal/store/projectDetails/actions'
import { projectUsersLoad } from 'portal/store/projectUsers/actions'
import { useRecordEditing } from 'portal/store/records/editing/useRecordEditing'
import { InfractionsContext } from 'portal/store/infractions/InfractionsContext'

import { Waste } from './Waste'
import { Delivery } from './Delivery'

const MODULE_VIEWS = {
  delivery: Delivery,
  waste: Waste
}

const NEW_TITLES = {
  waste: 'New Item',
  delivery: 'New Product'
}

const EDIT_TITLES = {
  waste: 'Edit Item',
  delivery: 'Edit Product'
}

export const LineItemEdit = ({ doneUri }) => {
  const dispatch = useDispatch()
  const { accountId, projectId, moduleName, lineItem } = useModuleIds()
  const moduleKey = moduleName.toLowerCase()
  const ModuleView = MODULE_VIEWS[moduleKey]

  const { loading: productGroupsLoading, error: productGroupsError } =
    useProductGroupsLoader()

  const allProductGroups = useSelector(state => state.productGroups)

  const projectTimeZone = useSelector(
    state => state.projectDetails.modules?.timeZone
  )
  const projectCountry = useSelector(
    state => state.projectDetails.modules?.country
  )
  const isImperial = imperial(projectCountry)

  // Check if we need to refresh project details
  useEffect(() => {
    if (!projectTimeZone || !projectCountry) {
      if (accountId && projectId) {
        dispatch(projectGetDetailsSaga(accountId, projectId))
      }
    }
  }, [accountId, dispatch, projectCountry, projectId, projectTimeZone])

  const isNewItem = lineItem.toLowerCase() === 'new'

  const editing = useRecordEditing()
  const recordDetail = useRecordDetailData()
  const lineItemData = useLineItemDataTransform(
    isNewItem,
    recordDetail.item,
    lineItem,
    isImperial,
    moduleKey
  )
  const loading =
    recordDetail.loading || editing.uploading || productGroupsLoading
  const error = recordDetail.error || editing.error || productGroupsError

  const { form, trackLineItemEditSave, trackLineItemEditCancel } =
    useFormWithDataAndTracking(
      isNewItem,
      !recordDetail.loading && !recordDetail.error,
      lineItemData
    )

  const { isProjectUser, roleLoaded } = useUserProjectRole(accountId, projectId)

  // Load Project Users in to Redux state
  useEffect(() => {
    if (roleLoaded && isProjectUser) {
      dispatch(projectUsersLoad(accountId, projectId, false))
    }
  }, [accountId, dispatch, isProjectUser, projectId, roleLoaded])

  const lineItemDataSourceTracking = useMemo(() => {
    if (moduleKey === 'delivery') {
      return recordDetail?.item?.dataSourceTracking?.products?.[lineItem] ?? {}
    }
    return recordDetail?.item?.dataSourceTracking?.items?.[lineItem] ?? {}
  }, [lineItem, moduleKey, recordDetail])

  const infractionsByName = useMemo(() => {
    if (isNewItem || !recordDetail?.infractions) {
      return null
    }

    return _.mapKeys(recordDetail?.infractions, (value, key) => {
      const [, index, keyFragment] = key.match(/data\.\w+\[(\d+)\]\.(.*)/) ?? []

      if (!index) {
        return '_notarrayitem_'
      }

      if (index !== lineItem) {
        return '_wrongindex_'
      }

      return keyFragment
    })
  }, [isNewItem, lineItem, recordDetail?.infractions])

  const { onEditSaveComplete, cancelUri } = useEditCompleteRedirection(doneUri)

  const submitItem = useCallback(
    formItem => {
      let item = formItem
      if (typeof ModuleView.transformFromFormData === 'function') {
        item = ModuleView.transformFromFormData(
          formItem,
          projectTimeZone,
          isImperial,
          allProductGroups,
          lineItemData.productGroups
        )
      }

      const record = produce(recordDetail.item, draftRecord => {
        const draftItems = draftRecord.data.items ?? draftRecord.data.products

        if (isNewItem) {
          item.id = uuid.v4()
          draftItems.push(item)
        } else {
          draftItems[lineItem] = _.mergeWith(
            draftItems[lineItem],
            item,
            (a, b) => (_.isArray(b) ? b : undefined)
          )
        }
      })

      const cleanedRecord = cleanWasteRecordForUpdate(record)

      trackLineItemEditSave()

      editing.submitRecord(cleanedRecord, onEditSaveComplete, isImperial)
    },
    [
      ModuleView,
      allProductGroups,
      editing,
      isImperial,
      isNewItem,
      lineItem,
      lineItemData.productGroups,
      onEditSaveComplete,
      projectTimeZone,
      recordDetail.item,
      trackLineItemEditSave
    ]
  )

  return (
    <LimitWidthContainer>
      {loading && <Spinner />}
      {error && (
        <Text error>Error! {typeof error === 'string' ? error : ''}</Text>
      )}

      {recordDetail.item && (
        <MainSection style={{ marginBottom: '0' }}>
          <FormProvider {...form}>
            <Form onSubmit={form.handleSubmit(submitItem)}>
              <Flex.Row amount={0} verticalCentre>
                <SubHeading
                  isChild
                  style={{ fontSize: '1.125rem', color: colours.CORE_NAVY }}
                >
                  {isNewItem ? NEW_TITLES[moduleKey] : EDIT_TITLES[moduleKey]}
                </SubHeading>

                <Flex.By amount={1} />

                <ApplyOrCancelControls
                  canApply={form.formState.isValid && form.formState.isDirty}
                  cancelUri={cancelUri}
                  onCancelled={trackLineItemEditCancel}
                />
              </Flex.Row>

              <SubSection style={{ marginTop: '1.25rem' }}>
                <InfractionsContext infractionsByFieldName={infractionsByName}>
                  <ModuleView
                    form={form}
                    isImperial={isImperial}
                    lineItemDataSourceTracking={lineItemDataSourceTracking}
                    currentProductGroup={
                      lineItemData &&
                      lineItemData.productGroups &&
                      lineItemData.productGroups[0]
                        ? {
                            label: lineItemData.productGroups[0].name,
                            value: lineItemData.productGroups[0].productGroupId,
                            key: lineItemData.productGroups[0].productGroupId
                          }
                        : null
                    }
                  />
                </InfractionsContext>
              </SubSection>
            </Form>
          </FormProvider>
        </MainSection>
      )}
    </LimitWidthContainer>
  )
}

LineItemEdit.propTypes = {
  doneUri: PropTypes.string.isRequired
}

function useEditCompleteRedirection(doneUri) {
  const navigate = useNavigate()

  return {
    onEditSaveComplete: () => {
      navigate(doneUri)
    },
    cancelUri: doneUri ?? undefined
  }
}

function useLineItemDataTransform(
  isNewItem,
  record,
  lineItemIndex,
  isImperial,
  moduleKey
) {
  return useMemo(() => {
    const data = record?.data
    if (!data) {
      return {}
    }

    const items = data.items ?? data.products
    const lineItem = { ...items[lineItemIndex] }
    // If isImperial and module is 'delivery' break Height, Width and Length Arrays in to separate fields
    if (isImperial && moduleKey === 'delivery') {
      lineItem.heightFoot = lineItem.height[0]?.value
      lineItem.heightInch = lineItem.height[1]?.value

      lineItem.widthFoot = lineItem.width[0]?.value
      lineItem.widthInch = lineItem.width[1]?.value

      lineItem.lengthFoot = lineItem.length[0]?.value
      lineItem.lengthInch = lineItem.length[1]?.value

      // Just return values for Volume and Weight fields
      if (
        lineItem.calculatedVolume &&
        Array.isArray(lineItem.calculatedVolume) &&
        lineItem.calculatedVolume.length > 0
      ) {
        lineItem.calculatedVolume = lineItem.calculatedVolume[0].value
      } else {
        lineItem.calculatedVolume = null
      }

      if (
        lineItem.givenVolume &&
        Array.isArray(lineItem.givenVolume) &&
        lineItem.givenVolume.length > 0
      ) {
        lineItem.givenVolume = lineItem.givenVolume[0].value
      } else {
        lineItem.givenVolume = null
      }

      if (
        lineItem.estimatedWeight &&
        Array.isArray(lineItem.estimatedWeight) &&
        lineItem.estimatedWeight.length > 0
      ) {
        lineItem.estimatedWeight = lineItem.estimatedWeight[0].value
      } else {
        lineItem.estimatedWeight = null
      }

      if (
        lineItem.weight &&
        Array.isArray(lineItem.weight) &&
        lineItem.weight.length > 0
      ) {
        lineItem.weight = lineItem.weight[0].value
      } else {
        lineItem.weight = null
      }
    }

    return isNewItem ? {} : (lineItem ?? {})
  }, [isImperial, isNewItem, lineItemIndex, moduleKey, record?.data])
}

function useFormWithDataAndTracking(isNewItem, ready, data) {
  const { moduleName } = useModuleIds()
  const moduleKey = moduleName.toLowerCase()
  const ModuleView = MODULE_VIEWS[moduleKey]

  const form = useForm({ defaultValues: {}, mode: 'all' })
  const { reset } = form

  const projectTimeZone = useSelector(
    state => state.projectDetails.modules?.timeZone
  )

  const {
    setInitialValues,
    trackLineItemEditSave,
    trackLineItemEditOpen,
    trackLineItemEditCancel
  } = useLineItemEditMixpanelEvents(form, isNewItem)

  useMountEffect(trackLineItemEditOpen)

  useEffect(() => {
    if (ready && data) {
      let formData = data
      if (typeof ModuleView.transformToFormData === 'function') {
        formData = ModuleView.transformToFormData(data, projectTimeZone)
      }
      formData = _.mapValues(formData, value => (value === 0 ? '0' : value))

      reset(formData)
      setInitialValues(formData)
    }
  }, [ModuleView, data, projectTimeZone, ready, reset, setInitialValues])

  return {
    form,
    trackLineItemEditSave,
    trackLineItemEditCancel
  }
}

const Form = styled.form`
  width: 100%;
  max-width: 80rem;

  align-self: center;
`
